import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MdEmail, MdPhone, MdLocationOn } from 'react-icons/md';
import { sendSimulationEmail } from '../services/emailService';

const Contact = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  // Formulaire principal (devis)
  const [devisForm, setDevisForm] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    energyType: '',
    consumption: '',
    solarPanels: '',
    battery: '',
    address: '',
    request: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          sectionRef.current.classList.add('visible');
          observer.disconnect();
        }
      }, { threshold: 0.2 });
      
      if (sectionRef.current) {
        observer.observe(sectionRef.current);
      }
      
      return () => observer.disconnect();
    }
  }, []);

  

  const handleDevisChange = (e) => {
    setDevisForm({ ...devisForm, [e.target.name]: e.target.value });
  };

  

  const handleDevisSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Validation basique
      if (!devisForm.name || !devisForm.email || !devisForm.phone || !devisForm.serviceType || !devisForm.address) {
        setSubmitStatus({
          type: 'error',
          message: 'Veuillez remplir tous les champs obligatoires.'
        });
        setIsSubmitting(false);
        return;
      }

      // Tentative d'envoi avec EmailJS
      console.log('Tentative d\'envoi du formulaire...', devisForm);
      const result = await sendSimulationEmail(devisForm);
      
      if (result.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Votre demande de simulation a été envoyée avec succès ! Nous vous recontacterons dans les 24h.'
        });
        
        // Réinitialiser le formulaire après succès
        setDevisForm({
          name: '',
          email: '',
          phone: '',
          serviceType: '',
          energyType: '',
          consumption: '',
          solarPanels: '',
          battery: '',
          address: '',
          request: ''
        });
      } else {
        // Afficher l'erreur EmailJS spécifique
        setSubmitStatus({
          type: 'error',
          message: `Échec de l'envoi: ${result.message}. Vérifiez la console pour plus de détails.`
        });
        console.error('Détails de l\'erreur:', result.error);
      }
    } catch (error) {
      console.error('Erreur inattendue lors de l\'envoi:', error);
      
      setSubmitStatus({
        type: 'error',
        message: `Une erreur inattendue s'est produite: ${error.message}. Contactez-nous directement.`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact section-animate" id="contact" ref={sectionRef}>
      <div className="container">
        <div className="section-header">
          <h2 className="section-title title-animate">{t('contact.title')}</h2>
          <p className="section-subtitle subtitle-animate">
            {t('contact.subtitle')}
          </p>
          <p className="section-subtitle2 subtitle-animate">
            {t('contact.subtitle2')}
          </p>
        </div>
        
        <div className="contact-content">
          <div className="contact-info">
            <div className="info-card stagger-item card-premium">
              <h3>{t('contact.info.title')}</h3>
              <p>{t('contact.info.description')}</p>
              
              <div className="contact-details">
                <div className="contact-item">
                  <span className="contact-icon"><MdEmail size={24} /></span>
                  <div>
                    <strong>{t('contact.email.label')}</strong>
                    <p>
                      <a href="mailto:contact@energy-bravo.be" className="contact-email">contact@energy-bravo.be</a>
                    </p>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon"><MdPhone size={24} /></span>
                  <div>
                    <strong>{t('contact.phone.label')}</strong>
                    <p>+32 2 123 45 67</p>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon"><MdLocationOn size={24} /></span>
                  <div>
                    <strong>{t('contact.address.label')}</strong>
                    <p>Rue de l'Énergie 123, 1000 Bruxelles</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-forms">
            <div className="form-card stagger-item card-premium">
              <h3>{t('contact.form.title')}</h3>
              <p>{t('contact.form.subtitle')}</p>
              
              <form onSubmit={handleDevisSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">{t('contact.form.name')} *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={devisForm.name}
                      onChange={handleDevisChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">{t('contact.form.email')} *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={devisForm.email}
                      onChange={handleDevisChange}
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">{t('contact.form.phone')} *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      placeholder={t('contact.form.phone.placeholder')}
                      value={devisForm.phone}
                      onChange={handleDevisChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="address">{t('contact.form.address')} *</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      required
                      value={devisForm.address}
                      onChange={handleDevisChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="serviceType">{t('contact.form.requestType')} *</label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    required
                    value={devisForm.serviceType}
                    onChange={handleDevisChange}
                  >
                    <option value="">{t('contact.form.selectRequest')}</option>
                    <option value="energie">{t('contact.form.energy')}</option>
                    <option value="solaire">{t('contact.form.solar')}</option>
                    <option value="coordination">{t('contact.form.coordination')}</option>
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="energyType">{t('contact.form.energyType')}</label>
                    <select
                      id="energyType"
                      name="energyType"
                      value={devisForm.energyType}
                      onChange={handleDevisChange}
                    >
                      <option value="">{t('contact.form.select')}</option>
                      <option value="electricite">{t('contact.form.electricityOnly')}</option>
                      <option value="gaz">{t('contact.form.gasOnly')}</option>
                      <option value="dual">{t('contact.form.electricityGas')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="consumption">{t('contact.form.annualConsumption')}</label>
                    <input
                      type="text"
                      id="consumption"
                      name="consumption"
                      placeholder="kWh/MWh"
                      value={devisForm.consumption}
                      onChange={handleDevisChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="solarPanels">{t('contact.form.panelsNumber')}</label>
                    <select
                      id="solarPanels"
                      name="solarPanels"
                      value={devisForm.solarPanels}
                      onChange={handleDevisChange}
                    >
                      <option value="">{t('contact.form.select')}</option>
                      <option value="1-5">{t('contact.form.panels1to5')}</option>
                      <option value="6-10">{t('contact.form.panels6to10')}</option>
                      <option value="11-15">{t('contact.form.panels11to15')}</option>
                      <option value="16+">{t('contact.form.panels16plus')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="battery">{t('contact.form.batteryDesired')}</label>
                    <select
                      id="battery"
                      name="battery"
                      value={devisForm.battery}
                      onChange={handleDevisChange}
                    >
                      <option value="">{t('contact.form.select')}</option>
                      <option value="oui">{t('contact.form.batteryYes')}</option>
                      <option value="non">{t('contact.form.batteryNo')}</option>
                      <option value="pas-sure">{t('contact.form.batteryNotSure')}</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="request">{t('contact.form.additionalInfo')}</label>
                  <textarea
                    id="request"
                    name="request"
                    rows="4"
                    placeholder={t('contact.form.describeNeeds')}
                    value={devisForm.request}
                    onChange={handleDevisChange}
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary btn-large"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Envoi en cours...' : t('contact.form.submit')}
                </button>
                
                {submitStatus && (
                  <div className={`submit-status ${submitStatus.type}`}>
                    {submitStatus.type === 'success' && '✅ '}
                    {submitStatus.type === 'error' && '❌ '}
                    {submitStatus.type === 'info' && 'ℹ️ '}
                    {submitStatus.message}
                  </div>
                )}
              </form>
              
              <p className="form-guarantee">
                {t('contact.form.guarantee')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 